import type * as React from 'react'
import { useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { plural, t } from '@lingui/core/macro'
import { Plural, Trans } from '@lingui/react/macro'

import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@jotta/ui/Dialog'
import { Divider } from '@jotta/ui/Divider'
import SvgLinkSimple from '@jotta/ui/SvgLinkSimple'
import SvgPhoto from '@jotta/ui/SvgPhoto'
import { cn } from '@jotta/utils/css'
import { createUrl } from '@jotta/utils/url'

import { CopyToClipboardCard } from './internals/CopyToClipboardCard'
import { ToggleCard } from './internals/ToggleCard'

export interface SharePhotoDialogProps {
  photoCount: number
  disabled?: boolean
  isLoading?: boolean
  onOpenChange: (open: boolean) => void
  onSharePhoto: (shared: boolean) => void
  shareId?: string
  triggerElement?: React.ReactNode
  forceOpen?: boolean
}

export const SharePhotosDialog: React.FC<SharePhotoDialogProps> = ({
  disabled,
  isLoading = false,
  photoCount,
  onOpenChange,
  onSharePhoto,
  shareId,
  triggerElement,
  forceOpen,
  ...props
}) => {
  const [open, setOpen] = useState(false)

  const isShared = Boolean(shareId)
  const shareLink = shareId ? createUrl('share', shareId) : ''

  return (
    <Dialog
      modal={true}
      open={open || forceOpen}
      onOpenChange={nextOpen => {
        if (!nextOpen && isLoading) {
          return
        }
        setOpen(nextOpen)
        onOpenChange(nextOpen)
      }}
      {...props}
    >
      {triggerElement && (
        <DialogTrigger asChild disabled={disabled}>
          {triggerElement}
        </DialogTrigger>
      )}
      <DialogContent>
        <DialogHeader className="flex flex-row items-center justify-normal gap-4">
          <PhotosThumbnail photoCount={photoCount} />
          <div>
            <DialogTitle>
              <Trans>Share</Trans>
            </DialogTitle>
            <DialogDescription>
              <Plural value={photoCount} one="# photo" other="# photos" />
            </DialogDescription>
          </div>
        </DialogHeader>
        <DialogBody className="grid">
          <ToggleCard
            label={plural(photoCount, {
              one: 'Share this photo',
              other: 'Share # photos',
            })}
            description={t`Viewable to anyone with the link`}
            icon={SvgLinkSimple}
            checked={isShared}
            onCheckedChange={onSharePhoto}
            loading={isLoading}
          />
          <AnimatePresence initial={false}>
            {isShared && (
              <motion.div
                className="grid"
                initial={{ gridTemplateRows: '0fr', opacity: 0 }}
                animate={{ gridTemplateRows: '1fr', opacity: 1 }}
                exit={{ gridTemplateRows: '0fr', opacity: 0 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
              >
                <div className="grid gap-5 overflow-hidden">
                  <Divider className="mt-5" />
                  <span className="label-sm">
                    <Trans>Link settings</Trans>
                  </span>
                  <CopyToClipboardCard
                    title={t`Share link`}
                    value={shareLink}
                  />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </DialogBody>
      </DialogContent>
    </Dialog>
  )
}

function PhotoThumbnailPlaceholder(props: React.ComponentProps<'div'>) {
  return (
    <div
      className={cn(
        'flex h-20 w-[88px] items-center justify-center rounded-2xl border-2 border-on-primary bg-surface-container-low',
        props.className,
      )}
    >
      <SvgPhoto width={32} />
    </div>
  )
}

function PhotosThumbnail({
  photoCount,
  className,
}: {
  photoCount: number
  className?: string
}) {
  const multiplePhotos = photoCount > 1

  return (
    <div className={cn('relative', className)}>
      <PhotoThumbnailPlaceholder
        className={cn({ '-rotate-6': multiplePhotos })}
      />
      {multiplePhotos && (
        <PhotoThumbnailPlaceholder className="absolute inset-0 rotate-6" />
      )}
    </div>
  )
}
